import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import {
  ArrowLeftIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ClockIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import Drawer from './Drawer';

const CATEGORY_OPTIONS = [
  { value: 'contract', label: 'Contract' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'letter', label: 'Letter' },
  { value: 'report', label: 'Report' },
  { value: 'work_order', label: 'Work Order' },
  { value: 'other', label: 'Other' }
];

const ENTITY_TYPE_OPTIONS = [
  { value: 'sale', label: 'Sale' },
  { value: 'contact', label: 'Contact' },
  { value: 'plot', label: 'Plot' },
  { value: 'work_order', label: 'Work Order' },
  { value: 'general', label: 'General' }
];

const PAGE_SIZE_OPTIONS = [
  { value: 'letter', label: 'Letter (8.5" x 11")' },
  { value: 'legal', label: 'Legal (8.5" x 14")' },
  { value: 'a4', label: 'A4 (210mm x 297mm)' }
];

// Available variables grouped by entity type
const AVAILABLE_VARIABLES = {
  global: [
    { key: 'siteName', label: 'Site Name', example: 'Parish Cemetery' },
    { key: 'currentDate', label: 'Current Date', example: '01/15/2026' },
    { key: 'currentYear', label: 'Current Year', example: '2026' }
  ],
  contact: [
    { key: 'contact.firstName', label: 'First Name', example: 'John' },
    { key: 'contact.lastName', label: 'Last Name', example: 'Smith' },
    { key: 'contact.fullName', label: 'Full Name', example: 'John Smith' },
    { key: 'contact.emailAddress', label: 'Email', example: 'john@example.com' },
    { key: 'contact.phoneNumber', label: 'Phone', example: '(555) 123-4567' },
    { key: 'contact.address', label: 'Address', example: '123 Main St' },
    { key: 'contact.city', label: 'City', example: 'Springfield' },
    { key: 'contact.state', label: 'State', example: 'IL' },
    { key: 'contact.zip', label: 'ZIP', example: '62701' },
    { key: 'contact.fullAddress', label: 'Full Address', example: '123 Main St, Springfield, IL 62701' }
  ],
  sale: [
    { key: 'sale.id', label: 'Sale ID', example: '1234' },
    { key: 'sale.status', label: 'Status', example: 'Completed' },
    { key: 'sale.saleDate', label: 'Sale Date', example: '01/15/2026' },
    { key: 'sale.subtotal', label: 'Subtotal', example: '$1,500.00', helper: 'formatCurrency' },
    { key: 'sale.tax', label: 'Tax', example: '$112.50', helper: 'formatCurrency' },
    { key: 'sale.total', label: 'Total', example: '$1,612.50', helper: 'formatCurrency' },
    { key: 'sale.amountPaid', label: 'Amount Paid', example: '$1,000.00', helper: 'formatCurrency' },
    { key: 'sale.balance', label: 'Balance Due', example: '$612.50', helper: 'formatCurrency' },
    { key: 'sale.notes', label: 'Notes', example: 'Special instructions...' }
  ],
  plot: [
    { key: 'plot.fid', label: 'Plot ID', example: '101' },
    { key: 'plot.alias', label: 'Plot Alias', example: 'A-101' },
    { key: 'plot.section', label: 'Section', example: 'Section A' },
    { key: 'plot.size', label: 'Size', example: '4x10' },
    { key: 'plot.isReserved', label: 'Is Reserved', example: 'Yes', helper: 'yesNo' }
  ],
  work_order: [
    { key: 'workOrder.id', label: 'Work Order ID', example: '5001' },
    { key: 'workOrder.title', label: 'Title', example: 'Headstone Installation' },
    { key: 'workOrder.description', label: 'Description', example: 'Install granite headstone...' },
    { key: 'workOrder.type', label: 'Type', example: 'Installation' },
    { key: 'workOrder.priority', label: 'Priority', example: 'High' },
    { key: 'workOrder.status', label: 'Status', example: 'In Progress' },
    { key: 'workOrder.dueDate', label: 'Due Date', example: '01/20/2026' }
  ]
};

const DEFAULT_CSS = `/* Document Styles */
body {
  font-family: 'Times New Roman', Times, serif;
  font-size: 12pt;
  line-height: 1.5;
  color: #333;
}

h1 { font-size: 18pt; margin-bottom: 12pt; }
h2 { font-size: 14pt; margin-bottom: 10pt; }
h3 { font-size: 12pt; margin-bottom: 8pt; }

.header { text-align: center; margin-bottom: 24pt; }
.footer { text-align: center; font-size: 10pt; color: #666; }

table { width: 100%; border-collapse: collapse; margin: 12pt 0; }
th, td { padding: 6pt 8pt; text-align: left; border-bottom: 1px solid #ddd; }
th { background-color: #f5f5f5; font-weight: bold; }

.total-row { font-weight: bold; border-top: 2px solid #333; }
.signature-line { margin-top: 48pt; border-top: 1px solid #333; width: 250px; }
`;

const DEFAULT_HTML = `<div class="header">
  <h1>{{siteName}}</h1>
  <p>Document Title</p>
</div>

<div class="content">
  <p>Date: {{currentDate}}</p>

  <!-- Add your content here -->
  <p>Dear {{contact.fullName}},</p>

  <p>Your document content goes here...</p>
</div>

<div class="footer">
  <p>Thank you for your business.</p>
</div>
`;

const DocumentTemplateEditor = () => {
  const { token } = useContext(AppContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');

  const isNew = !id;

  // Form state
  const [name, setName] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [entityType, setEntityType] = useState('general');
  const [htmlContent, setHtmlContent] = useState(DEFAULT_HTML);
  const [cssStyles, setCssStyles] = useState(DEFAULT_CSS);
  const [headerContent, setHeaderContent] = useState('');
  const [footerContent, setFooterContent] = useState('');
  const [pageSettings, setPageSettings] = useState({
    orientation: 'portrait',
    size: 'letter',
    margins: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 }
  });
  const [isActive, setIsActive] = useState(true);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('content');
  const [showVariables, setShowVariables] = useState(true);
  const [expandedVarGroups, setExpandedVarGroups] = useState(['global', 'contact']);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Version history state
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Auto-generate templateKey from name
  useEffect(() => {
    if (isNew && name && !templateKey) {
      const generated = name
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '_');
      setTemplateKey(generated);
    }
  }, [name, templateKey, isNew]);

  // Fetch template for editing or duplicating
  const fetchTemplate = useCallback(async (templateId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/document-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const template = await response.json();
        setName(duplicateId ? `${template.name} (Copy)` : template.name);
        setTemplateKey(duplicateId ? `${template.templateKey}_copy` : template.templateKey);
        setDescription(template.description || '');
        setCategory(template.category);
        setEntityType(template.entityType);
        setHtmlContent(template.htmlContent);
        setCssStyles(template.cssStyles || DEFAULT_CSS);
        setHeaderContent(template.headerContent || '');
        setFooterContent(template.footerContent || '');
        setPageSettings(template.pageSettings || {
          orientation: 'portrait',
          size: 'letter',
          margins: { top: 0.5, right: 0.5, bottom: 0.5, left: 0.5 }
        });
        setIsActive(template.isActive);
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to load template');
      }
    } catch (err) {
      console.error('Error fetching template:', err);
      setError('Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [token, duplicateId]);

  useEffect(() => {
    if (id) {
      fetchTemplate(id);
    } else if (duplicateId) {
      fetchTemplate(duplicateId);
    }
  }, [id, duplicateId, fetchTemplate]);

  // Fetch version history
  const fetchVersions = async () => {
    if (!id) return;

    try {
      setVersionsLoading(true);
      const response = await fetch(`/api/document-templates/${id}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setVersions(data);
      }
    } catch (err) {
      console.error('Error fetching versions:', err);
    } finally {
      setVersionsLoading(false);
    }
  };

  useEffect(() => {
    if (showVersions && id) {
      fetchVersions();
    }
  }, [showVersions, id]);

  // Handle save
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }
    if (!templateKey.trim()) {
      setError('Template key is required');
      return;
    }
    if (!htmlContent.trim()) {
      setError('Template content is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        name: name.trim(),
        templateKey: templateKey.trim(),
        description: description.trim(),
        category,
        entityType,
        htmlContent,
        cssStyles,
        headerContent,
        footerContent,
        pageSettings,
        isActive
      };

      const url = isNew || duplicateId
        ? '/api/document-templates'
        : `/api/document-templates/${id}`;

      const method = isNew || duplicateId ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const saved = await response.json();
        setSuccess('Template saved successfully');
        setTimeout(() => {
          if (isNew || duplicateId) {
            navigate(`/documents/templates/${saved.id}`);
          }
          setSuccess('');
        }, 1500);
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to save template');
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Handle preview
  const handlePreview = async () => {
    setShowPreview(true);
    setPreviewLoading(true);
    setPreviewHtml('');

    try {
      // For new templates, use inline preview
      if (isNew || duplicateId) {
        const response = await fetch('/api/document-templates/preview', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            htmlContent,
            cssStyles,
            headerContent,
            footerContent,
            entityType,
            pageSettings
          })
        });

        if (response.ok) {
          const data = await response.json();
          setPreviewHtml(data.html);
        } else {
          setPreviewHtml('<p style="color: red;">Failed to generate preview</p>');
        }
      } else {
        // For existing templates, use the template's preview endpoint
        const response = await fetch(`/api/document-templates/${id}/preview`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({})
        });

        if (response.ok) {
          const data = await response.json();
          setPreviewHtml(data.html);
        } else {
          setPreviewHtml('<p style="color: red;">Failed to generate preview</p>');
        }
      }
    } catch (err) {
      console.error('Error previewing template:', err);
      setPreviewHtml('<p style="color: red;">Failed to generate preview</p>');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle restore version
  const handleRestoreVersion = async (version) => {
    if (!window.confirm(`Restore template to version ${version.version}? This will save a new version with the restored content.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/document-templates/${id}/restore/${version.version}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const restored = await response.json();
        setHtmlContent(restored.htmlContent);
        setCssStyles(restored.cssStyles || DEFAULT_CSS);
        setHeaderContent(restored.headerContent || '');
        setFooterContent(restored.footerContent || '');
        setPageSettings(restored.pageSettings);
        setSuccess('Version restored successfully');
        setShowVersions(false);
        fetchVersions();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to restore version');
      }
    } catch (err) {
      console.error('Error restoring version:', err);
      setError('Failed to restore version');
    }
  };

  // Insert variable at cursor
  const insertVariable = (variable) => {
    const textarea = document.getElementById('html-content');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = htmlContent;

    let insertion = `{{${variable.key}}}`;
    if (variable.helper) {
      insertion = `{{${variable.helper} ${variable.key}}}`;
    }

    const newText = text.substring(0, start) + insertion + text.substring(end);
    setHtmlContent(newText);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + insertion.length, start + insertion.length);
    }, 0);
  };

  // Toggle variable group expansion
  const toggleVarGroup = (group) => {
    setExpandedVarGroups(prev =>
      prev.includes(group)
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  // Get relevant variable groups based on entity type
  const getRelevantVariableGroups = () => {
    const groups = [{ key: 'global', label: 'Global Variables', vars: AVAILABLE_VARIABLES.global }];

    if (entityType === 'contact' || entityType === 'sale' || entityType === 'general') {
      groups.push({ key: 'contact', label: 'Contact Variables', vars: AVAILABLE_VARIABLES.contact });
    }
    if (entityType === 'sale' || entityType === 'general') {
      groups.push({ key: 'sale', label: 'Sale Variables', vars: AVAILABLE_VARIABLES.sale });
    }
    if (entityType === 'plot' || entityType === 'sale' || entityType === 'general') {
      groups.push({ key: 'plot', label: 'Plot Variables', vars: AVAILABLE_VARIABLES.plot });
    }
    if (entityType === 'work_order' || entityType === 'general') {
      groups.push({ key: 'work_order', label: 'Work Order Variables', vars: AVAILABLE_VARIABLES.work_order });
    }

    return groups;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/documents/templates')}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isNew ? 'New Template' : duplicateId ? 'Duplicate Template' : 'Edit Template'}
            </h1>
            {!isNew && !duplicateId && (
              <p className="mt-1 text-sm text-gray-500">
                Version {pageSettings?.version || 1}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handlePreview}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            <EyeIcon className="h-5 w-5" />
            Preview
          </button>
          {!isNew && !duplicateId && (
            <button
              onClick={() => setShowVersions(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              <ClockIcon className="h-5 w-5" />
              Versions
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green-600 text-white rounded-md hover:bg-brand-green-700 disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-5 w-5" />
                Save Template
              </>
            )}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
          <button onClick={() => setError('')} className="float-right">×</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main content area */}
        <div className="lg:col-span-3 space-y-6">
          {/* Basic info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Template Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                  placeholder="e.g., Standard Sales Contract"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Key *
                </label>
                <input
                  type="text"
                  value={templateKey}
                  onChange={(e) => setTemplateKey(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                  placeholder="e.g., standard_sales_contract"
                />
                <p className="mt-1 text-xs text-gray-500">Unique identifier for this template</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                >
                  {CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Entity Type
                </label>
                <select
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                >
                  {ENTITY_TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">Determines where this template appears</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                  placeholder="Brief description of when to use this template..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded border-gray-300 text-brand-green-600 focus:ring-brand-green-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Template is active</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 ml-6">Inactive templates won't appear in document generation options</p>
              </div>
            </div>
          </div>

          {/* Content tabs */}
          <div className="bg-white rounded-lg shadow">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {[
                  { key: 'content', label: 'HTML Content' },
                  { key: 'styles', label: 'CSS Styles' },
                  { key: 'header', label: 'Header' },
                  { key: 'footer', label: 'Footer' },
                  { key: 'page', label: 'Page Settings' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 ${
                      activeTab === tab.key
                        ? 'border-brand-green-500 text-brand-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'content' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Template HTML *
                    </label>
                    <span className="text-xs text-gray-500">
                      Use {"{{variableName}}"} syntax for dynamic content
                    </span>
                  </div>
                  <textarea
                    id="html-content"
                    value={htmlContent}
                    onChange={(e) => setHtmlContent(e.target.value)}
                    rows={20}
                    className="w-full font-mono text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                    placeholder="Enter your HTML template content..."
                  />
                </div>
              )}

              {activeTab === 'styles' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      CSS Styles
                    </label>
                    <span className="text-xs text-gray-500">
                      Styles applied to the document
                    </span>
                  </div>
                  <textarea
                    value={cssStyles}
                    onChange={(e) => setCssStyles(e.target.value)}
                    rows={20}
                    className="w-full font-mono text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                    placeholder="/* Add your CSS styles here */"
                  />
                </div>
              )}

              {activeTab === 'header' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Header Content (Optional)
                    </label>
                    <span className="text-xs text-gray-500">
                      Appears at the top of each page
                    </span>
                  </div>
                  <textarea
                    value={headerContent}
                    onChange={(e) => setHeaderContent(e.target.value)}
                    rows={8}
                    className="w-full font-mono text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                    placeholder="<div class='header'>...</div>"
                  />
                </div>
              )}

              {activeTab === 'footer' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Footer Content (Optional)
                    </label>
                    <span className="text-xs text-gray-500">
                      Appears at the bottom of each page
                    </span>
                  </div>
                  <textarea
                    value={footerContent}
                    onChange={(e) => setFooterContent(e.target.value)}
                    rows={8}
                    className="w-full font-mono text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                    placeholder="<div class='footer'>Page {{page}} of {{pages}}</div>"
                  />
                </div>
              )}

              {activeTab === 'page' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Page Size
                      </label>
                      <select
                        value={pageSettings.size}
                        onChange={(e) => setPageSettings({ ...pageSettings, size: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                      >
                        {PAGE_SIZE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Orientation
                      </label>
                      <select
                        value={pageSettings.orientation}
                        onChange={(e) => setPageSettings({ ...pageSettings, orientation: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                      >
                        <option value="portrait">Portrait</option>
                        <option value="landscape">Landscape</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Margins (inches)
                    </label>
                    <div className="grid grid-cols-4 gap-4">
                      {['top', 'right', 'bottom', 'left'].map(side => (
                        <div key={side}>
                          <label className="block text-xs text-gray-500 mb-1 capitalize">{side}</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={pageSettings.margins?.[side] || 0.5}
                            onChange={(e) => setPageSettings({
                              ...pageSettings,
                              margins: {
                                ...pageSettings.margins,
                                [side]: parseFloat(e.target.value) || 0
                              }
                            })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Variables */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow sticky top-4">
            <div className="p-4 border-b border-gray-200">
              <button
                onClick={() => setShowVariables(!showVariables)}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-2">
                  <DocumentDuplicateIcon className="h-5 w-5 text-gray-400" />
                  <span className="font-medium text-gray-900">Variables</span>
                </div>
                {showVariables ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>

            {showVariables && (
              <div className="p-4 max-h-[600px] overflow-y-auto">
                <p className="text-xs text-gray-500 mb-4">
                  Click a variable to insert it at cursor position in the HTML content.
                </p>

                {getRelevantVariableGroups().map(group => (
                  <div key={group.key} className="mb-4">
                    <button
                      onClick={() => toggleVarGroup(group.key)}
                      className="flex items-center justify-between w-full text-left py-2"
                    >
                      <span className="text-sm font-medium text-gray-700">{group.label}</span>
                      {expandedVarGroups.includes(group.key) ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      )}
                    </button>

                    {expandedVarGroups.includes(group.key) && (
                      <div className="space-y-1">
                        {group.vars.map(variable => (
                          <button
                            key={variable.key}
                            onClick={() => insertVariable(variable)}
                            className="w-full text-left p-2 rounded hover:bg-gray-100 group"
                          >
                            <div className="text-xs font-mono text-brand-green-600">
                              {variable.helper
                                ? `{{${variable.helper} ${variable.key}}}`
                                : `{{${variable.key}}}`
                              }
                            </div>
                            <div className="text-xs text-gray-500 flex items-center justify-between">
                              <span>{variable.label}</span>
                              <span className="text-gray-400 group-hover:text-gray-600">{variable.example}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-blue-700">
                      <p className="font-medium mb-1">Handlebars Helpers</p>
                      <ul className="space-y-1">
                        <li><code>formatCurrency</code> - Format as $0.00</li>
                        <li><code>formatDate</code> - Format as MM/DD/YYYY</li>
                        <li><code>yesNo</code> - Convert boolean to Yes/No</li>
                        <li><code>uppercase</code> - Convert to uppercase</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview drawer */}
      <Drawer
        title="Document Preview"
        open={showPreview}
        setOpen={() => setShowPreview(false)}
        size="lg"
      >
        <div className="h-full overflow-auto">
          {previewLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green-600"></div>
            </div>
          ) : (
            <iframe
              srcDoc={previewHtml}
              className="w-full h-full min-h-[800px] border-0 bg-white"
              title="Document Preview"
            />
          )}
        </div>
      </Drawer>

      {/* Version history drawer */}
      <Drawer
        title="Version History"
        open={showVersions}
        setOpen={() => setShowVersions(false)}
      >
        <div className="p-4">
          {versionsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green-600"></div>
            </div>
          ) : versions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No version history available</p>
          ) : (
            <div className="space-y-4">
              {versions.map((version) => (
                <div
                  key={version.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Version {version.version}</span>
                    <button
                      onClick={() => handleRestoreVersion(version)}
                      className="text-sm text-brand-green-600 hover:text-brand-green-700"
                    >
                      Restore
                    </button>
                  </div>
                  <div className="text-sm text-gray-500">
                    <p>Changed by: {version.editor?.name || 'Unknown'}</p>
                    <p>Date: {new Date(version.createdAt).toLocaleString()}</p>
                    {version.changeNotes && <p>Notes: {version.changeNotes}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default DocumentTemplateEditor;
